import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Coins, Loader2, RefreshCw, Shield, ShoppingBag, Sparkles, X, Zap } from 'lucide-react';
import {
  CREDIT_POLICY_COPY_V2,
  IMAGEMOTION_STANDARD_CREDIT_COST,
  PHOTO_CLEAR_CREDIT_COST,
  REHUB_CUSTOM_CREDIT_PRICING,
  REHUB_LOGO_URL,
  STAGING_STANDARD_CREDIT_COST,
  clampCustomCredits,
  customCreditsTotalMinor,
  formatPackPriceFromMinor,
  formatPerCreditFromMinor,
  getCustomCreditStripePriceId,
  getPackStripePriceId,
  getPackStripePriceIdV2,
  getSubscriptionStripePriceId,
  getSubscriptionStripePriceIdV2,
  getUpsellSuggestion,
  minorToMajor,
  packsForCustomer,
  plansForCustomer,
  persistBillingCurrency,
  readPersistedBillingCurrency,
  readStoredCreditCurrency,
  storeCreditCurrency,
  subscriptionSavingsPercent,
  canCheckoutInCurrency,
  isCreditPolicyV3SalesEnabled,
  REHUB_CREDIT_ANNUAL_PLANS_V3,
  REHUB_CREDIT_MONTHLY_PLANS_V3,
  REHUB_CREDIT_TOPUPS_V3,
  quoteV3Plan,
  CREDIT_POLICY_COPY_V3,
  alternativeUsageExamples,
  REHUB_CURRENCIES,
  CURRENCY_LABEL,
  CURRENCY_SHORT_LABEL,
  CURRENCY_ACCESSIBLE_LABEL,
  type CreditCurrency,
  type RehubCreditSubscriptionDefinitionV2,
  type RehubPricingVersion,
} from '@rehub/pricing';
import type { CreateCreditCheckoutParams } from './checkout';
import { createCommerceTracker } from './commerce-track';

export type BuyCreditsAccent = 'orange' | 'purple' | 'amber';

export type BuyCreditsAnalyticsEvent =
  | 'buy_credits_panel_opened'
  | 'credit_pack_viewed'
  | 'credit_pack_selected'
  | 'credit_checkout_started'
  | 'credit_purchase_completed'
  | 'credit_purchase_failed'
  | 'credit_currency_changed'
  | 'custom_credit_purchase_selected';

export interface BuyCreditsAnalyticsProps {
  pack_id?: string;
  credits?: number;
  currency?: CreditCurrency;
  app_source?: string;
  user_segment?: 'new' | 'returning';
  offer_eligible?: boolean;
  purchase_type?: string;
  [key: string]: string | number | boolean | undefined;
}

export type { CreateCreditCheckoutParams };

export interface BuyCreditsModalProps {
  userId: string;
  credits: number;
  onClose: () => void;
  /** Create a Stripe Checkout session; return the redirect URL. */
  createCheckout: (params: CreateCreditCheckoutParams) => Promise<string>;
  /** App identifier for analytics / success URLs (e.g. "photoclear"). */
  appSource: string;
  accent?: BuyCreditsAccent;
  /** Optional slot above regular packs (e.g. ImageMotion double-credit offer). */
  offerSlot?: ReactNode | ((ctx: { currency: CreditCurrency }) => ReactNode);
  showMonthlyPlans?: boolean;
  welcomeOfferEligible?: boolean;
  userSegment?: 'new' | 'returning';
  onAnalytics?: (event: BuyCreditsAnalyticsEvent, props?: BuyCreditsAnalyticsProps) => void;
  /** Extra footer line (e.g. HDR credit copy). */
  footerNote?: ReactNode;
  pricingVersion?: RehubPricingVersion;
  lockedCurrency?: CreditCurrency | null;
  neededCredits?: number;
  returnPath?: string;
  /** Highlight this pack or plan without purchasing it. */
  initialProductId?: string;
  /** Present when this wallet already has a credit subscription. */
  activeCreditSubscription?: {
    planLabel: string;
    renewsAt?: string | null;
    cancelAtPeriodEnd?: boolean;
    legacy?: boolean;
  } | null;
  onManagePlan?: () => void;
  /** Schedule a plan or interval change for the next renewal. Grants nothing now. */
  schedulePlanChange?: (planId: string) => Promise<void>;
}

type BillingTab = 'topup' | 'subscription';

function isV2Plan(product: object): product is RehubCreditSubscriptionDefinitionV2 {
  return (
    'pricingVersion' in product &&
    (product as { pricingVersion?: string }).pricingVersion === 'v2' &&
    'features' in product &&
    Array.isArray((product as { features?: unknown }).features)
  );
}

function monthCoverageLines(credits: number): string[] {
  const photos = Math.floor(credits / Math.max(1, PHOTO_CLEAR_CREDIT_COST));
  const staged = Math.floor(credits / Math.max(1, STAGING_STANDARD_CREDIT_COST));
  const motion = Math.floor(credits / Math.max(1, IMAGEMOTION_STANDARD_CREDIT_COST));
  return [
    `About ${photos} PhotoClear declutters`,
    `${staged} standard staged images`,
    `${motion} ImageMotion animations`,
  ];
}

const ACCENT: Record<
  BuyCreditsAccent,
  {
    primary: string;
    primaryHover: string;
    border: string;
    soft: string;
    text: string;
    ring: string;
    btn: string;
    chip: string;
    orb: string;
    glow: string;
  }
> = {
  orange: {
    primary: 'bg-orange-600 hover:bg-orange-500',
    primaryHover: 'bg-orange-600',
    border: 'border-orange-400/80',
    soft: 'bg-gradient-to-b from-orange-500/20 via-orange-950/40 to-slate-950',
    text: 'text-orange-300',
    ring: 'focus-visible:ring-orange-400',
    btn: 'bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 shadow-lg shadow-orange-900/40',
    chip: 'bg-orange-500/15 text-orange-200 border-orange-400/30',
    orb: 'bg-orange-500/25',
    glow: 'shadow-orange-500/20',
  },
  purple: {
    primary: 'bg-purple-600 hover:bg-purple-500',
    primaryHover: 'bg-purple-600',
    border: 'border-purple-400/80',
    soft: 'bg-gradient-to-b from-purple-500/20 via-purple-950/40 to-slate-950',
    text: 'text-purple-300',
    ring: 'focus-visible:ring-purple-400',
    btn: 'bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-500 hover:to-fuchsia-400 shadow-lg shadow-purple-900/40',
    chip: 'bg-purple-500/15 text-purple-200 border-purple-400/30',
    orb: 'bg-purple-500/25',
    glow: 'shadow-purple-500/20',
  },
  amber: {
    primary: 'bg-amber-600 hover:bg-amber-500',
    primaryHover: 'bg-amber-600',
    border: 'border-amber-400/80',
    soft: 'bg-gradient-to-b from-amber-500/20 via-amber-950/40 to-slate-950',
    text: 'text-amber-300',
    ring: 'focus-visible:ring-amber-400',
    btn: 'bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 shadow-lg shadow-amber-900/40',
    chip: 'bg-amber-500/15 text-amber-200 border-amber-400/30',
    orb: 'bg-amber-500/25',
    glow: 'shadow-amber-500/20',
  },
};

function viteEnv(): Record<string, string | undefined> {
  try {
    return ((import.meta as unknown as { env?: Record<string, string | undefined> }).env) || {};
  } catch {
    return {};
  }
}

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  userId,
  credits,
  onClose,
  createCheckout,
  appSource,
  accent = 'orange',
  offerSlot,
  showMonthlyPlans = true,
  welcomeOfferEligible = false,
  userSegment,
  onAnalytics,
  footerNote,
  pricingVersion = 'v2',
  lockedCurrency = null,
  neededCredits,
  returnPath,
  initialProductId,
  activeCreditSubscription = null,
  onManagePlan,
  schedulePlanChange,
}) => {
  const resolvedInitialTab: BillingTab = plansForCustomer(pricingVersion).some((p) => p.id === initialProductId)
    ? 'subscription'
    : 'topup';
  const [billingTab, setBillingTab] = useState<BillingTab>(resolvedInitialTab);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [currency, setCurrency] = useState<CreditCurrency>(
    () => lockedCurrency || readPersistedBillingCurrency() || readStoredCreditCurrency('GBP')
  );
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customCreditsAmount, setCustomCreditsAmount] = useState(25);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const openedTracked = useRef(false);
  const packsViewed = useRef(false);
  const a = ACCENT[accent];
  const env = viteEnv();
  const v3Sales = isCreditPolicyV3SalesEnabled(env) && pricingVersion !== 'v1';

  const commerceTrack = createCommerceTracker();
  const track = (event: BuyCreditsAnalyticsEvent, props: BuyCreditsAnalyticsProps = {}) => {
    const payload = {
      app_source: appSource,
      currency,
      user_segment: userSegment,
      offer_eligible: welcomeOfferEligible,
      ...props,
    };
    onAnalytics?.(event, payload);
    if (event === 'credit_pack_viewed' || event === 'credit_checkout_started') {
      commerceTrack(
        event === 'credit_pack_viewed' ? 'credit_pack_viewed' : 'credit_purchase_started',
        payload
      );
    }
  };

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Open at the top of the panel; return focus to close for keyboard users.
    scrollRef.current?.scrollTo({ top: 0 });
    closeRef.current?.focus();

    if (!openedTracked.current) {
      openedTracked.current = true;
      track('buy_credits_panel_opened');
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per mount
  }, [onClose]);

  useEffect(() => {
    if (billingTab !== 'topup' || packsViewed.current) return;
    packsViewed.current = true;
    for (const pack of packsForCustomer(pricingVersion)) {
      track('credit_pack_viewed', { pack_id: pack.id, credits: pack.credits });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingTab]);

  const setCurrencyAndPersist = (next: CreditCurrency) => {
    if (lockedCurrency || next === currency) return;
    setCurrency(next);
    storeCreditCurrency(next);
    persistBillingCurrency(next);
    track('credit_currency_changed', { currency: next });
  };

  const redirectToCheckout = async (params: CreateCreditCheckoutParams, loadingId: string) => {
    setError(null);
    if (!userId) {
      setError('You must be signed in to purchase credits.');
      return;
    }
    const catalogueBacked = v3Sales && (params.purchaseType === 'credit_subscription' || params.purchaseType === 'credit_pack');
    if (!catalogueBacked && !canCheckoutInCurrency({ currency: params.currency, stripePriceId: params.priceId })) {
      setError('EUR checkout is not enabled yet. Switch to £ or $ to purchase, or check back shortly.');
      return;
    }
    setProcessingId(loadingId);
    track('credit_checkout_started', {
      pack_id: params.packId || params.planId,
      credits: params.credits,
      purchase_type: params.purchaseType,
    });
    try {
      const url = await createCheckout(params);
      window.location.href = url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Checkout failed.';
      track('credit_purchase_failed', {
        pack_id: params.packId || params.planId,
        credits: params.credits,
        purchase_type: params.purchaseType,
      });
      setError(message);
      setProcessingId(null);
    }
  };

  const handlePackCheckout = (packId: string) => {
    const pack = packsForCustomer(pricingVersion).find((p) => p.id === packId);
    if (!pack) return;
    track('credit_pack_selected', { pack_id: pack.id, credits: pack.credits });
    const priceId =
      'pricingVersion' in pack
        ? getPackStripePriceIdV2(pack, currency, env)
        : getPackStripePriceId(pack, currency, env);
    void redirectToCheckout(
      {
        packId: pack.id,
        currency,
        mode: 'payment',
        credits: pack.credits,
        priceId: priceId || undefined,
        userId,
        purchaseType: 'credit_pack',
        successUrl: `https://hub.rehub.studio/#/purchase-complete?app=${encodeURIComponent(appSource)}&type=credits&credits=${pack.credits}&return=${encodeURIComponent(returnPath || window.location.href)}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}${window.location.pathname || '/'}?credits=canceled`,
      },
      pack.id
    );
  };

  const handlePlanCheckout = (planId: string) => {
    if (v3Sales) {
      const plan = [...REHUB_CREDIT_MONTHLY_PLANS_V3, ...REHUB_CREDIT_ANNUAL_PLANS_V3].find((row) => row.id === planId);
      if (!plan) return;
      if (activeCreditSubscription && schedulePlanChange) {
        setProcessingId(planId);
        void schedulePlanChange(plan.id)
          .then(() => setError(null))
          .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not schedule that change.'))
          .finally(() => setProcessingId(null));
        return;
      }
      void redirectToCheckout(
        {
          planId: plan.id,
          currency,
          mode: 'subscription',
          credits: plan.credits,
          userId,
          purchaseType: 'credit_subscription',
          successUrl: `https://hub.rehub.studio/#/purchase-complete?app=${encodeURIComponent(appSource)}&type=subscription&credits=${plan.credits}&return=${encodeURIComponent(returnPath || window.location.href)}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}${window.location.pathname || '/'}?credits=canceled`,
        },
        plan.id
      );
      return;
    }
    const plan = plansForCustomer(pricingVersion).find((p) => p.id === planId);
    if (!plan) return;
    const priceId =
      'pricingVersion' in plan
        ? getSubscriptionStripePriceIdV2(plan, currency, env)
        : getSubscriptionStripePriceId(plan, currency, env);
    if (!priceId) {
      setError('This plan is not configured yet. Please try another option or contact support.');
      return;
    }
    void redirectToCheckout(
      {
        planId: plan.id,
        currency,
        mode: 'subscription',
        credits: plan.credits,
        priceId,
        userId,
        purchaseType: 'credit_subscription',
        successUrl: `https://hub.rehub.studio/#/purchase-complete?app=${encodeURIComponent(appSource)}&type=credits&credits=${plan.credits}&return=${encodeURIComponent(returnPath || window.location.href)}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}${window.location.pathname || '/'}?credits=canceled`,
      },
      plan.id
    );
  };

  const handleCustomCheckout = () => {
    const qty = clampCustomCredits(customCreditsAmount);
    const priceId = getCustomCreditStripePriceId(currency, env);
    if (!priceId) {
      setError('Exact-credit pricing is not configured. Please buy a pack or contact support.');
      return;
    }
    track('custom_credit_purchase_selected', { credits: qty });
    void redirectToCheckout(
      {
        currency,
        mode: 'payment',
        credits: qty,
        priceId,
        quantity: qty,
        userId,
        purchaseType: 'custom_credits',
        successUrl: `https://hub.rehub.studio/#/purchase-complete?app=${encodeURIComponent(appSource)}&type=credits&credits=${qty}&return=${encodeURIComponent(returnPath || window.location.href)}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}${window.location.pathname || '/'}?credits=canceled`,
      },
      `custom_${qty}`
    );
  };

  const v3Plans = billingInterval === 'year' ? REHUB_CREDIT_ANNUAL_PLANS_V3 : REHUB_CREDIT_MONTHLY_PLANS_V3;
  const products = v3Sales
    ? billingTab === 'subscription'
      ? v3Plans.map((plan) => {
          const quote = quoteV3Plan(plan, currency);
          return {
            ...plan,
            amountMinor: plan.amountMinor,
            perCredit: {
              GBP: quoteV3Plan(plan, 'GBP').perCreditLabel,
              EUR: quoteV3Plan(plan, 'EUR').perCreditLabel,
              USD: quoteV3Plan(plan, 'USD').perCreditLabel,
            },
            includedUsers: plan.seats,
            quote,
          };
        })
      : REHUB_CREDIT_TOPUPS_V3.map((pack) => ({
          ...pack,
          label: `${pack.credits} Credits`,
          ctaLabel: `Buy ${pack.credits} credits`,
          amountMinor: pack.amountMinor,
          perCredit: {
            GBP: `${(pack.amountMinor.GBP / pack.credits).toFixed(1)}p`,
            EUR: `${(pack.amountMinor.EUR / pack.credits).toFixed(1)}c`,
            USD: `${(pack.amountMinor.USD / pack.credits).toFixed(1)}¢`,
          },
        }))
    : billingTab === 'subscription'
      ? plansForCustomer(pricingVersion)
      : packsForCustomer(pricingVersion);
  const upsell = getUpsellSuggestion({
    currentPlanId: pricingVersion === 'v1' ? 'starter' : 'free_v2',
    recentPaygCredits: 0,
  });

  const customPerMajor = minorToMajor(REHUB_CUSTOM_CREDIT_PRICING.amountMinorPerCredit[currency]);
  const customTotalMinor = customCreditsTotalMinor(customCreditsAmount, currency);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{`
        @keyframes rehub-buy-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rehub-orb { 0%, 100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(18px,-10px,0) scale(1.08); } }
        @keyframes rehub-shimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        .rehub-buy-in { animation: rehub-buy-in .42s ease both; }
        .rehub-orb { animation: rehub-orb 8s ease-in-out infinite; }
        .rehub-shimmer { background-size: 200% 100%; animation: rehub-shimmer 3.2s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .rehub-buy-in, .rehub-orb, .rehub-shimmer { animation: none !important; }
        }
      `}</style>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="buy-credits-title"
        className="relative overflow-hidden bg-slate-950 border border-white/10 rounded-t-3xl sm:rounded-3xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-[0_30px_80px_-20px_rgba(0,0,0,0.75)]"
      >
        <div aria-hidden className={`pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full blur-3xl rehub-orb ${a.orb}`} />
        <div aria-hidden className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl rehub-orb" style={{ animationDelay: '1.6s' }} />
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_50%_at_10%_0%,rgba(255,255,255,0.06),transparent_55%)]" />

        <div className="relative flex items-start sm:items-center justify-between gap-3 p-4 sm:p-5 shrink-0 border-b border-white/10 bg-gradient-to-r from-white/[0.04] via-transparent to-white/[0.02]">
          <div className="flex items-center gap-3 min-w-0">
            <a
              href="https://hub.rehub.studio"
              target="_blank"
              rel="noopener noreferrer"
              className={`shrink-0 focus:outline-none ${a.ring} rounded-lg`}
            >
              <img
                src={REHUB_LOGO_URL}
                alt="ReHub Studio"
                className="h-8 sm:h-10 w-auto object-contain"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  if (el.dataset.fallback !== '1') {
                    el.dataset.fallback = '1';
                    el.src = '/ReHub-Logo-White.png';
                    return;
                  }
                  el.style.display = 'none';
                }}
              />
            </a>
            <div className="min-w-0">
              <h2 id="buy-credits-title" className="text-lg font-bold text-white truncate flex items-center gap-2">
                Buy Credits
                <Sparkles size={14} className={`${a.text} shrink-0`} aria-hidden />
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${a.chip}`}>
                  <Coins size={12} aria-hidden />
                  {credits.toLocaleString()} credits
                </span>
                <span className="text-[11px] text-slate-400">One wallet · every ReHub app</span>
              </div>
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className={`p-2.5 hover:bg-white/10 rounded-xl transition-colors focus:outline-none ${a.ring} shrink-0`}
            aria-label="Close buy credits panel"
          >
            <X size={18} className="text-slate-400" aria-hidden />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="relative p-4 sm:p-5 space-y-5 overflow-y-auto overscroll-contain flex-1 min-h-0"
        >
          {typeof offerSlot === 'function' ? offerSlot({ currency }) : offerSlot}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {showMonthlyPlans ? (
              <div className="flex items-center bg-slate-900/80 border border-white/10 rounded-xl p-1 w-full sm:w-auto" role="tablist" aria-label="Billing type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={billingTab === 'topup'}
                  onClick={() => setBillingTab('topup')}
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all focus:outline-none ${a.ring} ${
                    billingTab === 'topup' ? `${a.btn} text-white` : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <ShoppingBag size={14} aria-hidden />
                  One-time Top Up
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={billingTab === 'subscription'}
                  onClick={() => setBillingTab('subscription')}
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all focus:outline-none ${a.ring} ${
                    billingTab === 'subscription' ? `${a.btn} text-white` : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <RefreshCw size={14} aria-hidden />
                  {v3Sales ? 'Subscription' : 'Monthly Plan'}
                </button>
              </div>
            ) : (
              <h3 className="text-sm font-semibold text-slate-200">Credit packs</h3>
            )}
            <div className="flex flex-col items-end gap-1 self-end sm:self-auto" role="group" aria-label="Currency">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Currency</span>
              <div className="flex items-center bg-slate-900/80 border border-white/10 rounded-xl p-1">
                {REHUB_CURRENCIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    disabled={!!lockedCurrency}
                    aria-pressed={currency === c}
                    aria-label={CURRENCY_ACCESSIBLE_LABEL[c]}
                    title={CURRENCY_ACCESSIBLE_LABEL[c]}
                    onClick={() => setCurrencyAndPersist(c)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all focus:outline-none ${a.ring} ${
                      currency === c ? 'bg-white/10 text-white shadow-inner' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    <span className="sm:hidden">{CURRENCY_SHORT_LABEL[c]}</span>
                    <span className="hidden sm:inline">{CURRENCY_LABEL[c]}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {neededCredits != null && neededCredits > credits && (
            <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/15 to-orange-500/10 p-3 text-sm text-amber-50">
              <p className="font-semibold">You need {neededCredits} credits to create this.</p>
              <p className="text-amber-100/80">Current balance: {credits.toLocaleString()} credits</p>
            </div>
          )}
          {credits > 0 && credits <= 5 && (
            <p className="text-sm text-amber-200">{CREDIT_POLICY_COPY_V2.lowBalance}</p>
          )}
          {upsell && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-200">
              <p className="font-semibold">{upsell.headline}</p>
              <p className="text-slate-400">{upsell.body}</p>
            </div>
          )}

          {v3Sales && activeCreditSubscription && billingTab === 'subscription' && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-200">
              <p className="font-semibold">
                {activeCreditSubscription.legacy ? 'Current plan' : 'Your plan'}: {activeCreditSubscription.planLabel}
              </p>
              <p className="text-slate-400 mt-1">
                {activeCreditSubscription.cancelAtPeriodEnd
                  ? `Renewal is off${activeCreditSubscription.renewsAt ? ` and the plan ends on ${activeCreditSubscription.renewsAt}` : ''}. Paid credits keep their existing expiry dates.`
                  : activeCreditSubscription.renewsAt
                    ? `Renews on ${activeCreditSubscription.renewsAt}.`
                    : CREDIT_POLICY_COPY_V3.managePlan}
              </p>
              {onManagePlan && (
                <button type="button" onClick={onManagePlan} className="mt-2 text-sm font-semibold underline">
                  Manage plan
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-2xl" role="alert">
              <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" aria-hidden />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <div>
            <div className="flex items-end justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-white">
                  {billingTab === 'topup' ? 'Pick a top-up' : v3Sales && billingInterval === 'year' ? 'Pick an annual plan' : v3Sales ? 'Pick a monthly plan' : 'Pick a monthly plan'}
                </h3>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed max-w-2xl">
                  {billingTab === 'topup'
                    ? v3Sales
                      ? CREDIT_POLICY_COPY_V3.topUp
                      : 'Pay once. Credits land in your shared wallet and never expire.'
                    : v3Sales
                      ? billingInterval === 'year'
                        ? CREDIT_POLICY_COPY_V3.annual
                        : CREDIT_POLICY_COPY_V3.monthly
                      : 'A fresh grant every month, one shared wallet, and unused credits roll over up to your plan cap.'}
                </p>
              </div>
              {v3Sales && billingTab === 'subscription' && (
                <div className="flex bg-slate-900/80 border border-white/10 rounded-xl p-1" role="group" aria-label="Billing interval">
                  <button
                    type="button"
                    aria-pressed={billingInterval === 'month'}
                    onClick={() => setBillingInterval('month')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${billingInterval === 'month' ? 'bg-white/10 text-white' : 'text-slate-400'}`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    aria-pressed={billingInterval === 'year'}
                    onClick={() => setBillingInterval('year')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${billingInterval === 'year' ? 'bg-white/10 text-white' : 'text-slate-400'}`}
                  >
                    Annual
                  </button>
                </div>
              )}
            </div>
            <div
              key={billingTab}
              className={`grid gap-3 ${
                billingTab === 'topup'
                  ? products.length <= 4
                    ? 'grid-cols-2 md:grid-cols-4'
                    : 'grid-cols-2 sm:grid-cols-3'
                  : 'grid-cols-1 sm:grid-cols-3'
              }`}
            >
              {products.map((product, index) => {
                const isPack = billingTab === 'topup';
                const amountMinor = product.amountMinor[currency];
                const popular = product.badge === 'popular';
                const highVolume = 'badge' in product && product.badge === 'high-volume';
                const isProcessing = processingId === product.id;
                const v2Plan = !isPack && !v3Sales && isV2Plan(product) ? product : null;
                const v3Quote = !isPack && 'quote' in product ? (product as { quote?: ReturnType<typeof quoteV3Plan> }).quote : undefined;
                const savePct = v2Plan ? subscriptionSavingsPercent(v2Plan, currency) : 0;
                const includes = !isPack
                  ? [
                      ...(v3Quote
                        ? alternativeUsageExamples(product.credits).map((example) => `Or about ${example.count.toLocaleString()} ${example.label}`)
                        : monthCoverageLines(product.credits)),
                      ...(v3Quote
                        ? [
                            v3Quote.seats === 1 ? '1 user seat' : `Team access for ${v3Quote.seats} users`,
                            CREDIT_POLICY_COPY_V3.examplesAreAlternatives,
                          ]
                        : v2Plan
                        ? [
                            `Rollover up to ${v2Plan.rolloverCap.toLocaleString()} credits`,
                            v2Plan.includedUsers === 1
                              ? '1 user seat'
                              : `Team access for ${v2Plan.includedUsers} users`,
                            'Use across every ReHub AI tool',
                          ]
                        : ['Shared wallet across ReHub apps', 'Cancel anytime']),
                    ]
                  : [];
                const cta = isPack
                  ? ('ctaLabel' in product ? product.ctaLabel : `Buy ${product.credits} credits`)
                  : activeCreditSubscription && v3Sales
                    ? 'Switch at next renewal'
                    : 'Subscribe';
                const popularTheme = !isPack;
                const cardBorder = popular
                  ? popularTheme
                    ? 'border-purple-400/70 bg-gradient-to-b from-purple-500/20 via-purple-950/50 to-slate-950 shadow-lg shadow-purple-500/15'
                    : `${a.border} ${a.soft} shadow-lg ${a.glow}`
                  : 'border-white/10 bg-white/[0.035] hover:border-white/25 hover:bg-white/[0.055]';
                const cardBtn = popular
                  ? popularTheme
                    ? 'bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-500 hover:to-fuchsia-400 text-white shadow-lg shadow-purple-900/40'
                    : `${a.btn} text-white`
                  : 'bg-white/10 hover:bg-white/15 text-slate-100 border border-white/10';
                const badgeClass = popular
                  ? popularTheme
                    ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 rehub-shimmer'
                    : `${a.btn} rehub-shimmer`
                  : 'bg-slate-700/90';
                const perCreditDisplay = isPack
                  ? product.perCredit[currency]
                  : formatPerCreditFromMinor(amountMinor, product.credits, currency);
                const checkClass = popularTheme && popular ? 'text-purple-300' : a.text;

                return (
                  <div
                    key={product.id}
                    className={`group relative flex flex-col p-4 rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rehub-buy-in ${
                      isPack ? 'min-h-[188px]' : ''
                    } ${cardBorder} ${initialProductId === product.id ? 'ring-2 ring-white/70' : ''}`}
                    style={{ animationDelay: `${index * 55}ms` }}
                  >
                    <div aria-hidden className="pointer-events-none absolute -right-6 -top-8 opacity-[0.08] group-hover:opacity-[0.16] transition-opacity">
                      <Coins size={88} />
                    </div>
                    {(popular || highVolume) && (
                      <div
                        className={`absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 text-white text-[10px] font-bold rounded-full uppercase tracking-wide ${badgeClass}`}
                      >
                        {popular ? 'Popular' : 'High volume'}
                      </div>
                    )}
                    <div className={`relative mb-3 flex-1 ${isPack ? 'text-center' : 'text-left'}`}>
                      {!isPack && 'label' in product && (
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">
                          {product.label}
                        </div>
                      )}
                      <div className={`${isPack ? 'text-3xl' : 'text-4xl'} font-black tracking-tight text-white tabular-nums`}>
                        {product.credits.toLocaleString()}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {isPack ? 'credits' : v3Quote?.plan.interval === 'year' ? 'credits upfront' : 'credits / month'}
                      </div>
                      <div className={`text-xl font-bold mt-2 ${popular ? checkClass : 'text-white'}`}>
                        {v3Quote ? v3Quote.billedLabel : formatPackPriceFromMinor(amountMinor, currency)}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {v3Quote ? (
                          <>
                            {v3Quote.perCreditLabel} per credit
                            {v3Quote.equivalentLabel ? ` · ${v3Quote.equivalentLabel}` : ''}
                          </>
                        ) : (
                          <>
                            {perCreditDisplay} per credit
                            {!isPack ? ' · billed monthly' : ''}
                          </>
                        )}
                      </div>
                      {isPack && !v3Sales && (
                        <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.chip}`}>
                          <Sparkles size={10} aria-hidden /> Never expires
                        </div>
                      )}
                      {v3Quote?.savingLabel && (
                        <div className={`text-[11px] font-semibold mt-2 ${checkClass}`}>{v3Quote.savingLabel}</div>
                      )}
                      {v3Quote && (
                        <p className="mt-2 text-[11px] leading-snug text-slate-300">{v3Quote.validityLabel}</p>
                      )}
                      {savePct > 0 && (
                        <div className={`text-[11px] font-semibold mt-2 ${checkClass}`}>
                          Save {savePct}% vs PAYG
                        </div>
                      )}
                      {includes.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {includes.map((line) => (
                            <li key={line} className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-300/90">
                              <Check size={12} className={`mt-0.5 shrink-0 ${checkClass}`} aria-hidden />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => (isPack ? handlePackCheckout(product.id) : handlePlanCheckout(product.id))}
                      disabled={!!processingId}
                      className={`relative w-full py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 focus:outline-none ${a.ring} ${cardBtn}`}
                    >
                      {isProcessing ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 size={14} className="animate-spin" aria-hidden /> Loading…
                        </span>
                      ) : (
                        cta
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {billingTab === 'topup' && pricingVersion === 'v1' && (
            <div className="space-y-3 pt-2 border-t border-white/10">
              <div>
                <h3 className="text-sm font-semibold text-slate-200">Need a different amount?</h3>
                <p className="text-xs text-slate-500 mt-0.5">Buy an exact number of credits.</p>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Zap size={18} className={`${a.text} flex-shrink-0`} aria-hidden />
                  <span className="font-semibold text-white">Buy exact credits</span>
                </div>
                <p className="text-sm text-slate-400">
                  Minimum of {REHUB_CUSTOM_CREDIT_PRICING.minCredits} credits. Pay only for the credits you
                  need — no pack discount.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={REHUB_CUSTOM_CREDIT_PRICING.minCredits}
                    max={REHUB_CUSTOM_CREDIT_PRICING.maxCredits}
                    value={customCreditsAmount}
                    aria-label="Custom credit amount"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!e.target.value.trim()) return;
                      setCustomCreditsAmount(clampCustomCredits(isNaN(v) ? REHUB_CUSTOM_CREDIT_PRICING.minCredits : v));
                    }}
                    onBlur={() => setCustomCreditsAmount((c) => clampCustomCredits(c))}
                    className={`w-24 bg-slate-950 border border-white/15 rounded-lg px-3 py-2 text-white font-semibold text-center focus:outline-none ${a.ring}`}
                  />
                  <span className="text-slate-400">credits</span>
                </div>
                <div className="text-slate-400 text-sm">
                  {formatPackPriceFromMinor(REHUB_CUSTOM_CREDIT_PRICING.amountMinorPerCredit[currency], currency)}{' '}
                  per credit · Total: {formatPackPriceFromMinor(customTotalMinor, currency)}
                  <span className="sr-only"> ({customPerMajor.toFixed(2)} major units per credit)</span>
                </div>
                <button
                  type="button"
                  onClick={handleCustomCheckout}
                  disabled={!!processingId}
                  className={`w-full py-2.5 rounded-xl font-semibold ${a.btn} text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50 focus:outline-none ${a.ring}`}
                >
                  {processingId?.startsWith('custom_') ? (
                    <>
                      <Loader2 size={16} className="animate-spin" aria-hidden /> Loading…
                    </>
                  ) : (
                    `Buy ${clampCustomCredits(customCreditsAmount)} credits`
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2 pb-1">
            {footerNote}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400">
              <Shield size={11} className={a.text} aria-hidden /> Stripe checkout
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400">
              <Sparkles size={11} className={a.text} aria-hidden />
              {v3Sales
                ? billingTab === 'subscription'
                  ? billingInterval === 'year'
                    ? CREDIT_POLICY_COPY_V3.annual
                    : CREDIT_POLICY_COPY_V3.monthly
                  : CREDIT_POLICY_COPY_V3.topUp
                : pricingVersion === 'v2'
                  ? 'Purchased credits never expire'
                  : 'Credits last 12 months'}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400">
              <RefreshCw size={11} className={a.text} aria-hidden />
              {billingTab === 'subscription' ? 'Cancel anytime in Stripe' : 'Subscription credits roll over'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;
